import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Plus, CalendarBlank, Users, ArrowRight, ChartBar, WarningOctagon } from '@phosphor-icons/react';
import useAuthStore from '../../store/authStore';
import { useTenantPath } from '../../lib/tenantPath';
import { API, inr, fmtDate, PeriodStatusPill } from './_shared';

import { InlineSkeleton } from '../../components/Skeletons';
/**
 * /reviews landing — split into two visual sections:
 *   1. "What needs my attention" — open commitment windows for the logged-in
 *      user. Renders first, big + urgent.
 *   2. "All cycles" — cycles this user has access to (VP+ sees all; others
 *      see only cycles they're a member of).
 *
 * VP+ get a "New Cycle" primary CTA at the top-right.
 */
export default function ReviewCenter() {
  const { user } = useAuthStore();
  const tp = useTenantPath();
  const [cycles, setCycles] = useState([]);
  const [active, setActive] = useState([]);
  const [loading, setLoading] = useState(true);

  const isVpPlus = ['Vice President', 'President'].includes(user?.role);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/reviews/cycles`),
      axios.get(`${API}/reviews/my-active`),
    ]).then(([c, a]) => {
      setCycles(c.data.cycles || []);
      setActive(a.data.active_periods || []);
    }).catch((e) => console.error('Load reviews failed', e))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="review-center">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <p className="text-xs uppercase tracking-widest text-[#92400E] font-bold">Team Reviews</p>
          <h1 className="app-page-title" style={{ fontFamily: 'Outfit' }}>Review Center</h1>
          <p className="text-sm text-[#6D8E87] mt-1">
            Set targets. Commit against them. Review actuals against commitment.
          </p>
        </div>
        {isVpPlus && (
          <Link to={tp('/reviews/new')} className="btn-primary flex items-center gap-2" data-testid="new-cycle-btn">
            <Plus size={18} weight="bold" /> New Cycle
          </Link>
        )}
      </div>

      {/* Active commitment windows — highest urgency, shown first */}
      {active.length > 0 && (
        <section className="mb-8" data-testid="active-commitments">
          <h2 className="text-lg font-bold text-[#052A23] mb-3 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            <WarningOctagon size={20} className="text-amber-600" weight="duotone" />
            Needs your attention
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {active.map((row) => {
              const p = row.period;
              const c = row.my_commitment;
              const live = p.live_status;
              const canCommit = live === 'commitment_open';
              return (
                <Link
                  key={p.id}
                  to={canCommit ? tp(`/reviews/commit/${p.id}`) : tp(`/reviews/periods/${p.id}`)}
                  className="medical-card p-4 hover:border-[#00a69c] transition group"
                  data-testid={`active-period-${p.id}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0">
                      <div className="text-xs uppercase tracking-widest text-[#6D8E87] font-bold">{p.cycle_title}</div>
                      <div className="text-base font-bold text-[#052A23] mt-0.5">Review: {fmtDate(p.review_date)}</div>
                    </div>
                    <PeriodStatusPill status={live} />
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center border-t border-[#D5E8E4] pt-3">
                    <div>
                      <div className="text-[9px] uppercase text-[#6D8E87]">Target</div>
                      <div className="text-sm font-bold text-[#052A23]">{inr(row.my_target)}</div>
                    </div>
                    <div>
                      <div className="text-[9px] uppercase text-[#6D8E87]">Committed</div>
                      <div className="text-sm font-bold text-[#00a69c]">{c ? inr(c.committed_value) : '—'}</div>
                    </div>
                    <div>
                      <div className="text-[9px] uppercase text-[#6D8E87]">Coverage</div>
                      <div className={`text-sm font-bold ${c && c.coverage_pct >= 100 ? 'text-emerald-600' : c ? 'text-amber-600' : 'text-slate-400'}`}>
                        {c ? `${c.coverage_pct}%` : '—'}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 pt-2 border-t border-[#D5E8E4] flex items-center justify-between text-xs">
                    <span className="text-[#6D8E87]">
                      {canCommit ? (c ? 'Update your commitment' : 'Commit now') : 'View details'}
                    </span>
                    <ArrowRight size={14} className="text-[#00a69c] group-hover:translate-x-1 transition" />
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* All cycles */}
      <section>
        <h2 className="text-lg font-bold text-[#052A23] mb-3" style={{ fontFamily: 'Outfit' }}>
          All Cycles {cycles.length ? `(${cycles.length})` : ''}
        </h2>
        {loading ? (
          <InlineSkeleton />
        ) : cycles.length === 0 ? (
          <div className="medical-card p-8 text-center">
            <ChartBar size={40} className="text-[#D5E8E4] mx-auto mb-3" weight="duotone" />
            <p className="text-sm text-[#6D8E87] mb-4">
              {isVpPlus
                ? 'No review cycles yet. Kick off your first weekly/monthly review to get the team on a discipline.'
                : 'You are not in any active review cycle yet. Your manager will add you when it starts.'}
            </p>
            {isVpPlus && (
              <Link to={tp('/reviews/new')} className="btn-primary inline-flex items-center gap-2" data-testid="empty-new-cycle-btn">
                <Plus size={16} weight="bold" /> Create your first cycle
              </Link>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {cycles.map((c) => (
              <Link
                key={c.id}
                to={tp(`/reviews/cycles/${c.id}`)}
                className="medical-card p-4 hover:border-[#00a69c] transition group"
                data-testid={`cycle-card-${c.id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-[#92400E] font-bold flex items-center gap-1.5 flex-wrap">
                      <span>{c.frequency}</span>
                      {/* iter114 — OVERSEEING pill for cycles the viewer
                          is the in-charge of but not a committing member. */}
                      {c.viewer_is_in_charge && !c.viewer_is_member && (
                        <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest bg-[#F0E6D2] text-[#92400E] px-1.5 py-0.5 rounded border border-[#E4CFA1]" data-testid={`overseeing-pill-${c.id}`}>
                          Overseeing
                        </span>
                      )}
                    </div>
                    <div className="text-base font-bold text-[#052A23] truncate" title={c.title}>{c.title}</div>
                  </div>
                  <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${
                    c.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                    c.status === 'paused' ? 'bg-amber-100 text-amber-700' :
                    'bg-slate-100 text-slate-600'
                  }`}>
                    {c.status}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-xs text-[#6D8E87] mt-3">
                  <span className="inline-flex items-center gap-1"><Users size={12} /> {c.members?.length || 0} member{c.members?.length === 1 ? '' : 's'}</span>
                  <span className="inline-flex items-center gap-1"><CalendarBlank size={12} /> {c.frequency}</span>
                </div>
                <div className="text-[11px] text-[#6D8E87] mt-3 truncate">
                  by {c.created_by_name || 'unknown'}
                  {c.in_charge_user_name && (
                    <> · <span className="text-[#3B5A54]">In-charge: <b>{c.in_charge_user_name}</b></span></>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
